import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import FilterBar from '../components/ui/FilterBar';
import { formatDate } from '../lib/utils';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { 
  MessageSquare, 
  Plus, 
  Eye,
  Edit,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  User,
  Tag,
  X
} from 'lucide-react';

// Importar tipos compartilhados
import { Ticket, TicketStats } from '../types/ticket';

// Importar modais
import TicketViewModal from '../components/modals/TicketViewModal';
import TicketCreateModal from '../components/modals/TicketCreateModal';
import TicketMassMessageModal from '../components/modals/TicketMassMessageModal';


const Tickets: React.FC = () => {
  const { user, canCloseTickets, canDeleteTickets, isAdmin } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [ticketStats, setTicketStats] = useState<TicketStats>({
    total: 0,
    open: 0,
    inProgress: 0,
    waitingResponse: 0,
    closed: 0,
    resolved: 0,
    active: 0
  });

  // Debounce para busca (500ms de delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  // Filtros
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Opções para filtros
  const [statusOptions, setStatusOptions] = useState<Array<{value: string, label: string}>>([]);
  const [priorityOptions, setPriorityOptions] = useState<Array<{value: string, label: string}>>([]);
  const [categoryOptions, setCategoryOptions] = useState<Array<{value: string, label: string}>>([]);

  // Estados dos modais
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isMassMessageModalOpen, setIsMassMessageModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  
  // Estados para seleção em massa
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);

  const fetchTickets = useCallback(async () => {
    try {
      // Se for uma busca (não o carregamento inicial), mostrar indicador de busca
      if (debouncedSearchTerm || statusFilter || priorityFilter || categoryFilter) {
        setIsSearching(true);
      } else {
        setIsLoading(true);
      }
      
      const params: any = {
        page: currentPage.toString(),
        limit: '10'
      };
      
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;
      
      // Usar apiService em vez de fetch direto
      const data = await apiService.getTickets(params);
      const processedTickets = (data.data || []).map((ticket: any) => ({
          ...ticket,
          // Usar dados do usuário logado se createdBy for null
          createdBy: ticket.createdBy || {
            _id: user?.id || 'unknown',
            username: user?.username || 'Usuário Desconhecido',
            email: user?.email || 'n/a',
            name: user?.name || user?.username || 'Usuário Desconhecido',
            role: user?.level || 'user'
          },
          // Manter assignedTo como está (pode ser null)
          assignedTo: ticket.assignedTo || null,
          // Processar mensagens com dados do usuário se necessário
          messages: (ticket.messages || []).map((message: any) => ({
            ...message,
            sentBy: message.sentBy || {
              _id: user?.id || 'unknown',
              username: user?.username || 'Usuário Desconhecido',
              email: user?.email || 'n/a',
              name: user?.name || user?.username || 'Usuário Desconhecido',
              role: user?.level || 'user'
            }
          }))
        }));
        setTickets(processedTickets);
        setTotalPages(data.pagination?.pages || 1);
        setTotalTickets(data.pagination?.total || 0);
    } catch (error) {
      console.error('Erro ao buscar tickets:', error);
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, debouncedSearchTerm, statusFilter, priorityFilter, categoryFilter, user?.id, user?.username, user?.email, user?.name, user?.level]);

  const fetchTicketStats = useCallback(async () => {
    try {
      // Usar apiService em vez de fetch direto
      const data = await apiService.getTicketStats();
      setTicketStats(prevStats => data.data || prevStats);
    } catch (error) {
      console.error('Erro ao buscar estatísticas dos tickets:', error);
    }
  }, []);

  // Carregar opções dos filtros
  const loadFilterOptions = useCallback(async () => {
    try {
      // Definir opções fixas
      setStatusOptions([
        { value: 'open', label: 'Aberto' },
        { value: 'in_progress', label: 'Em Andamento' },
        { value: 'waiting_response', label: 'Aguardando Resposta' },
        { value: 'closed', label: 'Fechado' },
        { value: 'resolved', label: 'Resolvido' }
      ]);

      setPriorityOptions([
        { value: 'low', label: 'Baixa' },
        { value: 'medium', label: 'Média' },
        { value: 'high', label: 'Alta' },
        { value: 'urgent', label: 'Urgente' }
      ]);

      setCategoryOptions([
        { value: 'technical', label: 'Técnico' },
        { value: 'billing', label: 'Faturamento' },
        { value: 'account', label: 'Conta' },
        { value: 'feature_request', label: 'Solicitação de Funcionalidade' },
        { value: 'bug_report', label: 'Relatório de Bug' },
        { value: 'other', label: 'Outro' }
      ]);
    } catch (error) {
      console.error('Erro ao carregar opções dos filtros:', error);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    loadFilterOptions();
    fetchTicketStats();
  }, [fetchTickets, loadFilterOptions, fetchTicketStats]);

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setCurrentPage(1);
    fetchTickets();
  };

  const clearFilters = () => {
    setStatusFilter('');
    setPriorityFilter('');
    setCategoryFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Funções para abrir modais
  const openViewModal = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setIsViewModalOpen(true);
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const openMassMessageModal = () => {
    setIsMassMessageModalOpen(true);
  };

  // Funções para fechar modais
  const closeAllModals = () => {
    setIsViewModalOpen(false);
    setIsCreateModalOpen(false);
    setIsMassMessageModalOpen(false);
    setIsBulkDeleteModalOpen(false);
    setSelectedTicket(null);
  };

  // Funções para seleção em massa
  const toggleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map(ticket => ticket._id));
    }
  };

  const toggleSelectTicket = (ticketId: string) => {
    setSelectedTickets(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };


  // Função para obter ícone do status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <Edit className="h-4 w-4" />;
      case 'waiting_response': return <MessageSquare className="h-4 w-4" />;
      case 'closed': return <XCircle className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // Função para obter cor do status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-blue-600 bg-blue-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'waiting_response': return 'text-orange-600 bg-orange-100';
      case 'closed': return 'text-gray-600 bg-gray-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Função para obter cor da prioridade
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-blue-600 bg-blue-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'urgent': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Função para encerrar ticket
  const handleCloseTicket = async (ticketId: string) => {
    if (!canCloseTickets()) {
      alert('Você não tem permissão para encerrar tickets');
      return;
    }

    if (!window.confirm('Tem certeza que deseja encerrar este ticket?')) {
      return;
    }

    try {
      await apiService.updateTicketStatus(ticketId, 'closed', 'admin_closed');
      
      await fetchTickets();
      await fetchTicketStats();
      alert('Ticket encerrado com sucesso!');
    } catch (error) {
      console.error('Erro ao encerrar ticket:', error);
      alert('Erro ao encerrar ticket');
    }
  };

  // Função para deletar ticket
  const handleDeleteTicket = async (ticketId: string) => {
    if (!canDeleteTickets()) {
      alert('Você não tem permissão para deletar tickets');
      return;
    }

    if (!window.confirm('Tem certeza que deseja deletar este ticket? Esta ação não pode ser desfeita!')) {
      return;
    }

    try {
      await apiService.deleteTicket(ticketId);
      
      await fetchTickets();
      await fetchTicketStats();
      alert('Ticket deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar ticket:', error);
      alert('Erro ao deletar ticket');
    }
  };

  // Função para obter label do status
  const getStatusLabel = (status: string) => {
    const labels = {
      open: 'Aberto',
      in_progress: 'Em Andamento',
      waiting_response: 'Aguardando Resposta',
      closed: 'Fechado',
      resolved: 'Resolvido'
    };
    return labels[status as keyof typeof labels] || status;
  };

  // Função para obter label da prioridade
  const getPriorityLabel = (priority: string) => {
    const labels = {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      urgent: 'Urgente'
    };
    return labels[priority as keyof typeof labels] || priority;
  };

  // Função para obter label da categoria
  const getCategoryLabel = (category: string) => {
    const labels = {
      technical: 'Técnico',
      billing: 'Faturamento',
      account: 'Conta',
      feature_request: 'Solicitação de Funcionalidade',
      bug_report: 'Relatório de Bug',
      other: 'Outro'
    };
    return labels[category as keyof typeof labels] || category;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tickets</h1>
          <p className="text-gray-600">Gerenciar tickets de suporte</p>
        </div>
        <div className="flex gap-2">
          {isAdmin() && (
            <Button 
              onClick={openMassMessageModal}
              variant="outline"
              className="flex items-center gap-2"
            >
              <MessageSquare className="h-4 w-4" />
              Mensagem em Massa
            </Button>
          )}
          <Button 
            onClick={openCreateModal}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Ticket
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700">Total de Tickets</p>
                <p className="text-3xl font-bold text-blue-900">{ticketStats.total.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg">
                <MessageSquare className="h-6 w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Ativos</p>
                <p className="text-3xl font-bold text-green-900">{ticketStats.active.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-full shadow-lg">
                <Clock className="h-6 w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-700">Abertos</p>
                <p className="text-3xl font-bold text-yellow-900">{ticketStats.open.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full shadow-lg">
                <AlertCircle className="h-6 w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-700">Em Andamento</p>
                <p className="text-3xl font-bold text-orange-900">{ticketStats.inProgress.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full shadow-lg">
                <Edit className="h-6 w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-700">Aguardando</p>
                <p className="text-3xl font-bold text-purple-900">{ticketStats.waitingResponse.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full shadow-lg">
                <MessageSquare className="h-6 w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Fechados</p>
                <p className="text-3xl font-bold text-gray-900">{ticketStats.closed.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full shadow-lg">
                <XCircle className="h-6 w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-indigo-700">Resolvidos</p>
                <p className="text-3xl font-bold text-indigo-900">{ticketStats.resolved.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full shadow-lg">
                <CheckCircle className="h-6 w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Busca */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={() => handleSearch()}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        statusOptions={statusOptions}
        packageFilter={priorityFilter}
        onPackageFilterChange={setPriorityFilter}
        packageOptions={priorityOptions}
        trialFilter={categoryFilter}
        onTrialFilterChange={setCategoryFilter}
        trialOptions={categoryOptions}
        onClearFilters={clearFilters}
        showAdvancedFilters={true}
      />

      {/* Lista de Tickets */}
      <Card className="shadow-sm">
        <CardHeader className="bg-gray-50 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-sm">
                Lista de Tickets
                {isSearching && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                {totalTickets.toLocaleString()} tickets encontrados
                {isSearching && " (buscando...)"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">
                    {canDeleteTickets() && (
                      <input
                        type="checkbox"
                        checked={selectedTickets.length === tickets.length && tickets.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    )}
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">Título</th>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">Status</th>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">Prioridade</th>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">Categoria</th>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">Criado por</th>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">Atribuído a</th>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">Última Atividade</th>
                  <th className="text-left p-4 font-semibold text-gray-700 text-sm">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tickets.map((ticket, index) => (
                  <tr key={ticket._id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="p-4">
                      {canDeleteTickets() && (
                        <input
                          type="checkbox"
                          checked={selectedTickets.includes(ticket._id)}
                          onChange={() => toggleSelectTicket(ticket._id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      )}
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        <p className="font-semibold text-gray-900">{ticket.title}</p>
                        <p className="text-sm text-gray-500 truncate max-w-xs">{ticket.description}</p>
                        {ticket.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {ticket.tags.slice(0, 3).map((tag, tagIndex) => (
                              <span key={tagIndex} className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                                <Tag className="h-3 w-3 mr-1" />
                                {tag}
                              </span>
                            ))}
                            {ticket.tags.length > 3 && (
                              <span className="text-sm text-gray-500">+{ticket.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(ticket.status)}`}>
                        {getStatusIcon(ticket.status)}
                        {getStatusLabel(ticket.status)}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
                        {getPriorityLabel(ticket.priority)}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-gray-900">{getCategoryLabel(ticket.category)}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 truncate">{ticket.createdBy.name}</p>
                          <p className="text-sm text-gray-500 truncate">@{ticket.createdBy.username}</p>
                          <p className="text-xs text-gray-400 truncate">{ticket.createdBy.role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {ticket.assignedTo ? (
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-green-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">{ticket.assignedTo.name}</p>
                            <p className="text-sm text-gray-500 truncate">@{ticket.assignedTo.username}</p>
                            <p className="text-xs text-gray-400 truncate">{ticket.assignedTo.role}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-gray-400" />
                          </div>
                          <div>
                            <span className="text-gray-400 text-sm font-medium">Não atribuído</span>
                            <p className="text-xs text-gray-300">Aguardando atribuição</p>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-gray-900">
                        {formatDate(ticket.lastActivityAt)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {ticket.messageCount} mensagens
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openViewModal(ticket)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          Ver
                        </Button>
                        
                        {canCloseTickets() && ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCloseTicket(ticket._id)}
                            className="flex items-center gap-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <XCircle className="h-4 w-4" />
                            Encerrar
                          </Button>
                        )}
                        
                        {canDeleteTickets() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteTicket(ticket._id)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                            Deletar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
        
        {/* Ações em massa */}
        {selectedTickets.length > 0 && canDeleteTickets() && (
          <div className="bg-blue-50 border-t border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-blue-900">
                  {selectedTickets.length} ticket{selectedTickets.length > 1 ? 's' : ''} selecionado{selectedTickets.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedTickets([])}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Cancelar Seleção
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (selectedTickets.length === 0) {
                      alert('Selecione pelo menos um ticket para deletar');
                      return;
                    }
                    setIsBulkDeleteModalOpen(true);
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Deletar Selecionados
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Modais */}
      {isViewModalOpen && selectedTicket && (
        <TicketViewModal
          ticket={selectedTicket}
          isOpen={isViewModalOpen}
          onClose={closeAllModals}
          onTicketUpdate={fetchTickets}
        />
      )}

      {isCreateModalOpen && (
        <TicketCreateModal
          isOpen={isCreateModalOpen}
          onClose={closeAllModals}
          onTicketCreated={fetchTickets}
        />
      )}

      {isMassMessageModalOpen && isAdmin() && (
        <TicketMassMessageModal
          isOpen={isMassMessageModalOpen}
          onClose={closeAllModals}
          onMessageSent={fetchTickets}
        />
      )}

      {/* Modal de confirmação para deletar tickets em massa */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <X className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Confirmar Exclusão
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja deletar <strong>{selectedTickets.length}</strong> ticket{selectedTickets.length > 1 ? 's' : ''} selecionado{selectedTickets.length > 1 ? 's' : ''}?
              <br />
              <span className="text-red-600 font-medium">Esta ação não pode ser desfeita!</span>
            </p>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsBulkDeleteModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  if (!canDeleteTickets()) {
                    alert('Você não tem permissão para deletar tickets');
                    return;
                  }

                  try {
                    await apiService.deleteTicketsInBulk(selectedTickets);
                    setSelectedTickets([]);
                    setIsBulkDeleteModalOpen(false);
                    await fetchTickets();
                    await fetchTicketStats();
                    alert(`${selectedTickets.length} tickets deletados com sucesso!`);
                  } catch (error) {
                    console.error('Erro ao deletar tickets em massa:', error);
                    alert('Erro ao deletar tickets em massa');
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Deletar {selectedTickets.length} Ticket{selectedTickets.length > 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
